import ApiService from './api'

/**
 * Servicio especializado para gestión de configuración
 */
class ConfiguracionService {
  // ===== CONFIGURACIÓN DE EMPRESA =====
  
  /**
   * Obtiene la configuración de empresa
   */
  static async getEmpresaConfig() {
    try {
      const response = await ApiService.get('/configuracion/empresa')
      return response
    } catch (error) {
      console.error('Error al obtener configuración de empresa:', error)
      throw error
    }
  }

  /**
   * Actualiza la configuración de empresa
   */
  static async updateEmpresaConfig(data) {
    try {
      const response = await ApiService.put('/configuracion/empresa', data)
      return response
    } catch (error) {
      console.error('Error al actualizar configuración de empresa:', error)
      throw error
    }
  }

  // ===== CONFIGURACIÓN DE FACTURACIÓN =====

  /**
   * Obtiene la configuración de facturación para la empresa activa
   */
  static async getFacturacionConfig() {
    try {
      const response = await ApiService.get('/configuracion/facturacion')
      return response
    } catch (error) {
      console.error('Error al obtener configuración de facturación:', error)
      throw error
    }
  }

  /**
   * Actualiza la configuración de facturación (admite FormData)
   */
  static async updateFacturacionConfig(data) {
    try {
      if (data instanceof FormData) {
        // PHP no parsea multipart/form-data en PUT — usar POST
        const response = await ApiService.post('/configuracion/facturacion', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response
      }
      const response = await ApiService.put('/configuracion/facturacion', data)
      return response
    } catch (error) {
      console.error('Error al actualizar configuración de facturación:', error)
      throw error
    }
  }

  /**
   * Solicita el paso a ambiente de producción ante el SRI
   */
  static async requestAmbienteProduccion() {
    try {
      const response = await ApiService.post('/configuracion/facturacion/solicitar-produccion')
      return response
    } catch (error) {
      console.error('Error al solicitar ambiente de producción:', error)
      throw error
    }
  }

  /**
   * Obtiene el catálogo de tipos de agentes de retención
   */
  static async getTiposAgenteRetencion() {
    try {
      const response = await ApiService.get('/configuracion/facturacion/agentes')
      return response
    } catch (error) {
      console.error('Error al obtener tipos de agentes de retención:', error)
      throw error
    }
  }

  /**
   * Intenta extraer metadatos del certificado digital antes de guardarlo
   */
  static async previewCertificadoDigital(formData) {
    try {
      const response = await ApiService.post('/configuracion/facturacion/certificado/previsualizar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response
    } catch (error) {
      console.error('Error al previsualizar certificado digital:', error)
      throw error
    }
  }

  // ===== GESTIÓN DE USUARIOS =====
  
  /**
   * Obtiene la lista de usuarios de la empresa
   */
  static async getUsuarios() {
    try {
      const response = await ApiService.get('/configuracion/usuarios')
      return response
    } catch (error) {
      console.error('Error al obtener usuarios:', error)
      throw error
    }
  }

  /**
   * Obtiene la lista de usuarios de cliente de la empresa
   */
  static async getClientUsuarios(search = '', estado = '') {
    try {
      const params = new URLSearchParams()
      if (search) params.append('busqueda', search)
      if (estado) params.append('estado', estado)
      
      const url = `/configuracion/client-usuarios${params.toString() ? '?' + params.toString() : ''}`
      const response = await ApiService.get(url)
      return response
    } catch (error) {
      console.error('Error al obtener usuarios de cliente:', error)
      throw error
    }
  }

  /**
   * Crea un nuevo usuario
   */
  static async createUsuario(userData) {
    try {
      const response = await ApiService.post('/configuracion/usuarios', userData)
      return response
    } catch (error) {
      console.error('Error al crear usuario:', error)
      throw error
    }
  }

  /**
   * Actualiza un usuario existente
   */
  static async updateUsuario(userId, userData) {
    try {
      const response = await ApiService.put(`/configuracion/usuarios/${userId}`, userData)
      return response
    } catch (error) {
      console.error('Error al actualizar usuario:', error)
      throw error
    }
  }

  /**
   * Elimina un usuario
   */
  static async deleteUsuario(userId) {
    try {
      const response = await ApiService.delete(`/configuracion/usuarios/${userId}`)
      return response
    } catch (error) {
      console.error('Error al eliminar usuario:', error)
      throw error
    }
  }

  // ===== GESTIÓN DE PERMISOS =====
  
  /**
   * Obtiene los permisos de un usuario
   */
  static async getPermisosUsuario(userId) {
    try {
      const response = await ApiService.get(`/configuracion/permisos/${userId}`)
      return response
    } catch (error) {
      console.error('Error al obtener permisos del usuario:', error)
      throw error
    }
  }

  /**
   * Actualiza los permisos de un usuario
   */
  static async updatePermisosUsuario(userId, permisos) {
    try {
      const response = await ApiService.put(`/configuracion/permisos/${userId}`, { permisos })
      return response
    } catch (error) {
      console.error('Error al actualizar permisos del usuario:', error)
      throw error
    }
  }

  /**
   * Obtiene todos los permisos disponibles en el sistema
   */
  static async getPermisosDisponibles() {
    try {
      const response = await ApiService.get('/configuracion/permisos/disponibles')
      return response
    } catch (error) {
      console.error('Error al obtener permisos disponibles:', error)
      throw error
    }
  }

  // ===== PARÁMETROS DEL SISTEMA =====
  
  /**
   * Obtiene todos los parámetros del sistema organizados por categoría
   */
  static async getParametros() {
    try {
      const response = await ApiService.get('/configuracion/parametros')
      return response
    } catch (error) {
      console.error('Error al obtener parámetros:', error)
      throw error
    }
  }

  /**
   * Actualiza los parámetros del sistema
   */
  static async updateParametros(parametros) {
    try {
      const response = await ApiService.put('/configuracion/parametros', parametros)
      return response
    } catch (error) {
      console.error('Error al actualizar parámetros:', error)
      throw error
    }
  }

  /**
   * Obtiene un parámetro específico
   */
  static async getParametro(categoria, clave) {
    try {
      const response = await ApiService.get(`/configuracion/parametros/${categoria}/${clave}`)
      return response
    } catch (error) {
      console.error('Error al obtener parámetro:', error)
      throw error
    }
  }

  /**
   * Actualiza un parámetro específico
   */
  static async updateParametro(categoria, clave, valor) {
    try {
      const response = await ApiService.put(`/configuracion/parametros/${categoria}/${clave}`, { valor })
      return response
    } catch (error) {
      console.error('Error al actualizar parámetro:', error)
      throw error
    }
  }

  // ===== UTILIDADES DE CONFIGURACIÓN =====
  
  /**
   * Valida la configuración de empresa
   */
  static validateEmpresaConfig(data) {
    const errors = {}

    if (!data.business_name || data.business_name.trim() === '') {
      errors.business_name = 'La razón social es requerida'
    }

    if (!data.trade_name || data.trade_name.trim() === '') {
      errors.trade_name = 'El nombre comercial es requerido'
    }

    if (!data.ruc || data.ruc.trim() === '') {
      errors.ruc = 'El RUC es requerido'
    } else if (!/^\d{13}$/.test(data.ruc)) {
      errors.ruc = 'El RUC debe tener 13 dígitos'
    }

    if (!data.email || data.email.trim() === '') {
      errors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'El email no tiene un formato válido'
    }

    if (!data.address || data.address.trim() === '') {
      errors.address = 'La dirección es requerida'
    }

    if (!data.phone || data.phone.trim() === '') {
      errors.phone = 'El teléfono es requerido'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  /**
   * Valida los datos de un usuario
   */
  static validateUsuarioData(data, isEdit = false) {
    const errors = {}

    if (!data.username || data.username.trim() === '') {
      errors.username = 'El nombre de usuario es requerido'
    } else if (data.username.length < 3) {
      errors.username = 'El nombre de usuario debe tener al menos 3 caracteres'
    }

    if (!data.email || data.email.trim() === '') {
      errors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'El email no tiene un formato válido'
    }

    if (!data.full_name || data.full_name.trim() === '') {
      errors.full_name = 'El nombre completo es requerido'
    }

    if (!isEdit || (data.password && data.password.trim() !== '')) {
      if (!data.password || data.password.length < 8) {
        errors.password = 'La contraseña debe tener al menos 8 caracteres'
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  /**
   * Formatea los parámetros para envío al backend
   */
  static formatParametrosForAPI(parametros) {
    const formatted = {}

    Object.keys(parametros).forEach(categoria => {
      formatted[categoria] = {}
      
      Object.keys(parametros[categoria]).forEach(key => {
        let value = parametros[categoria][key]
        
        // Convertir boolean a string
        if (typeof value === 'boolean') {
          value = value ? '1' : '0'
        }
        
        // Convertir number a string
        if (typeof value === 'number') {
          value = value.toString()
        }
        
        formatted[categoria][key] = value
      })
    })

    return formatted
  }

  /**
   * Obtiene la configuración completa del sistema para un módulo específico
   */
  static async getConfiguracionModulo(modulo) {
    try {
      const response = await ApiService.get(`/configuracion/modulos/${modulo}`)
      return response
    } catch (error) {
      console.error(`Error al obtener configuración del módulo ${modulo}:`, error)
      throw error
    }
  }

  /**
   * Resetea la configuración de un módulo a valores por defecto
   */
  static async resetConfiguracionModulo(modulo) {
    try {
      const response = await ApiService.post(`/configuracion/modulos/${modulo}/reset`)
      return response
    } catch (error) {
      console.error(`Error al resetear configuración del módulo ${modulo}:`, error)
      throw error
    }
  }

  /**
   * Exporta la configuración completa del sistema
   */
  static async exportarConfiguracion() {
    try {
      const response = await ApiService.get('/configuracion/exportar', {
        responseType: 'blob'
      })
      return response
    } catch (error) {
      console.error('Error al exportar configuración:', error)
      throw error
    }
  }

  /**
   * Importa una configuración desde un archivo
   */
  static async importarConfiguracion(archivo) {
    try {
      const formData = new FormData()
      formData.append('configuracion', archivo)
      
      const response = await ApiService.post('/configuracion/importar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response
    } catch (error) {
      console.error('Error al importar configuración:', error)
      throw error
    }
  }
}

export default ConfiguracionService
